import type { Program, Stmt } from './ir.js';

/**
 * Words that cannot be variable names in any of the four views (plus our JS helpers). One
 * identifier map is shared by all views so a variable has the same name in every tab.
 */
const RESERVED = new Set(
  [
    // C++
    'int double float char bool string void return if else while for do break continue switch case',
    'default true false const static auto long short unsigned signed struct class namespace using',
    'std cin cout endl main include new delete this and or not xor',
    // Python
    'def elif pass in is None True False lambda import from as with try except finally raise',
    'global nonlocal yield del assert print input range len str float',
    // JavaScript
    'let var function null undefined typeof instanceof Math readNumber readDecimal readText write',
    // Pseudocod keywords
    'citeste scrie daca atunci altfel cat timp executa pentru repeta pana cand si sau',
  ]
    .join(' ')
    .split(' '),
);

const DIACRITICS: Record<string, string> = {
  ă: 'a',
  â: 'a',
  î: 'i',
  ș: 's',
  ş: 's',
  ț: 't',
  ţ: 't',
  Ă: 'A',
  Â: 'A',
  Î: 'I',
  Ș: 'S',
  Ş: 'S',
  Ț: 'T',
  Ţ: 'T',
};

function sanitize(name: string): string {
  let id = name
    .replace(/[ăâîșşțţĂÂÎȘŞȚŢ]/g, (c) => DIACRITICS[c] ?? c)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[^A-Za-z0-9_]/g, '');
  if (id === '') id = 'x';
  if (/^[0-9]/.test(id)) id = `_${id}`;
  if (RESERVED.has(id)) id = `${id}_`;
  return id;
}

/** Block variable name → identifier valid in C++, Python and JavaScript. */
export function identifierMap(program: Program): Map<string, string> {
  const map = new Map<string, string>();
  const used = new Set<string>();
  for (const name of program.variables) {
    const base = sanitize(name);
    let id = base;
    for (let n = 2; used.has(id); n++) id = `${base}${n}`;
    used.add(id);
    map.set(name, id);
  }
  return map;
}

const COUNTER_NAMES = ['i', 'j', 'k', 't', 'p', 'q'];

/**
 * Names for the hidden counters of "repeat n times" loops: one per nesting depth of repeat
 * loops, never clashing with a user variable.
 */
export function repeatCounters(program: Program, ids: Map<string, string>): Map<string, string> {
  const taken = new Set(ids.values());
  const free = COUNTER_NAMES.filter((n) => !taken.has(n));
  for (let i = 1; free.length < 8; i++) if (!taken.has(`k${i}`)) free.push(`k${i}`);
  const out = new Map<string, string>();
  const visit = (body: readonly Stmt[], depth: number) => {
    for (const s of body) {
      switch (s.k) {
        case 'repeat':
          out.set(s.id, free[Math.min(depth, free.length - 1)]!);
          visit(s.body, depth + 1);
          break;
        case 'if':
          for (const b of s.branches) visit(b.body, depth);
          if (s.orElse) visit(s.orElse, depth);
          break;
        case 'while':
        case 'for':
          visit(s.body, depth);
          break;
        default:
          break;
      }
    }
  };
  visit(program.body, 0);
  return out;
}
